import React, { useState, useEffect } from 'react';
import { 
  Archive,
  Calendar,
  AlertTriangle,
  X,
  Users,
  Shield
} from 'lucide-react';

/**
 * Modal d'archivage avancé pour les intervenants
 */
const ArchiveIntervenantModal = ({ 
  intervenant, 
  db, 
  onClose, 
  onSuccess 
}) => {
  const [archiveDate, setArchiveDate] = useState('');
  const [archiveReason, setArchiveReason] = useState('');
  const [freeAppointments, setFreeAppointments] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [roles, setRoles] = useState([]);

  // Pas de restriction de date - les dates antérieures sont autorisées
  // const today = new Date().toISOString().split('T')[0];

  // Récupérer les rôles depuis Firestore
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const { collection, getDocs } = await import('firebase/firestore');
        const rolesRef = collection(db, 'advanced_roles');
        const querySnapshot = await getDocs(rolesRef);
        const rolesData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setRoles(rolesData);
      } catch (error) {
        console.error('Erreur lors de la récupération des rôles:', error);
      }
    };

    if (db) {
      fetchRoles();
    }
  }, [db]);

  // Fonction pour obtenir le nom du rôle à partir de l'ID
  const getRoleName = (roleId) => {
    const role = roles.find(r => r.id === roleId);
    return role ? role.name : roleId; // Retourne le nom du rôle ou l'ID si non trouvé
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!archiveDate) {
      setError('Veuillez sélectionner une date d\'effet de l\'archivage');
      return;
    }

    if (!archiveReason.trim()) {
      setError('Veuillez indiquer la raison de l\'archivage');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Confirmation finale
      let confirmMessage;
      if (freeAppointments) {
        confirmMessage = `Êtes-vous sûr de vouloir archiver ${intervenant.firstName} ${intervenant.lastName} et libérer tous ses rendez-vous à partir du ${new Date(archiveDate).toLocaleDateString('fr-FR')} ?`;
      } else {
        confirmMessage = `Êtes-vous sûr de vouloir archiver ${intervenant.firstName} ${intervenant.lastName} ? Les rendez-vous resteront assignés mais ne pourront plus être modifiés.`;
      }
      
      if (!window.confirm(confirmMessage)) {
        setIsLoading(false);
        return;
      }

      // Import des fonctions Firestore nécessaires
      const { 
        updateDoc, 
        doc, 
        collection, 
        query, 
        where, 
        getDocs, 
        writeBatch,
        Timestamp 
      } = await import('firebase/firestore');

      // 1. Mettre à jour l'intervenant avec les informations d'archivage
      await updateDoc(doc(db, 'employees', intervenant.id), {
        isArchived: true,
        archiveDate: Timestamp.fromDate(new Date(archiveDate)),
        archiveReason: archiveReason.trim(),
        archivedAt: Timestamp.now(),
        // Note: archivedBy pourrait être ajouté si l'utilisateur courant est disponible
      });

      // 2. Libérer les rendez-vous si demandé
      if (freeAppointments) {
        const selectedDate = new Date(archiveDate);
        selectedDate.setHours(0, 0, 0, 0); // Début de la journée
        
        const appointmentsRef = collection(db, 'appointments');
        const q = query(
          appointmentsRef, 
          where('employeeId', '==', intervenant.id),
          where('date', '>=', Timestamp.fromDate(selectedDate))
        );
        
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
          const batch = writeBatch(db);
          let appointmentsFreed = 0;
          
          // Analyser les rendez-vous pour gérer les séries récurrentes
          const recurringSeries = new Map(); // Pour regrouper les séries récurrentes
          
          querySnapshot.forEach((document) => {
            const appointmentData = document.data();
            
            // Si c'est un rendez-vous récurrent avec une série
            if (appointmentData.isRecurring && appointmentData.recurrenceSeriesId) {
              const seriesId = appointmentData.recurrenceSeriesId;
              
              if (!recurringSeries.has(seriesId)) {
                recurringSeries.set(seriesId, []);
              }
              recurringSeries.get(seriesId).push({
                id: document.id,
                date: appointmentData.date,
                ...appointmentData
              });
            } else {
              // Rendez-vous individuel ou sans série - libérer directement
              appointmentsFreed++;
              batch.update(document.ref, {
                employeeId: null,
                status: 'libre',
                employeeName: ''
              });
            }
          });
          
          // Pour chaque série récurrente, libérer seulement les rendez-vous individuels
          // sans affecter la série complète
          for (const [seriesId, appointmentsInSeries] of recurringSeries) {
            appointmentsInSeries.forEach(appointment => {
              appointmentsFreed++;
              batch.update(doc(db, 'appointments', appointment.id), {
                employeeId: null,
                status: 'libre',
                employeeName: ''
              });
            });
            
            console.log(`Série récurrente ${seriesId}: ${appointmentsInSeries.length} rendez-vous libérés`);
          }
          
          await batch.commit();
          console.log(`${appointmentsFreed} rendez-vous libérés pour ${intervenant.firstName} ${intervenant.lastName}`);
          
          // Optionnel: Afficher un message d'information sur les séries récurrentes
          if (recurringSeries.size > 0) {
            console.log(`${recurringSeries.size} séries récurrentes ont été partiellement libérées (seulement les rendez-vous à partir de ${archiveDate})`);
          }
        }
      }

      // Succès
      if (onSuccess) {
        onSuccess({
          intervenantName: `${intervenant.firstName} ${intervenant.lastName}`,
          archiveDate,
          archiveReason,
          appointmentsFreed: freeAppointments
        });
      }

      onClose();

    } catch (error) {
      console.error('Erreur lors de l\'archivage:', error);
      setError('Une erreur est survenue lors de l\'archivage. Veuillez réessayer.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 dark:bg-opacity-70">
      <div
        className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* En-tête */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <Archive className="text-orange-500 mr-3" size={24} />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Archiver l'intervenant
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Contenu */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Informations de l'intervenant */}
          <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
            <div className="flex items-center">
              <Users className="text-blue-600 dark:text-blue-400 mr-3" size={20} />
              <div>
                <p className="text-blue-800 dark:text-blue-200 font-medium">
                  {intervenant.firstName} {intervenant.lastName}
                </p>
                {intervenant.email && (
                  <p className="text-blue-600 dark:text-blue-400 text-sm mt-1">{intervenant.email}</p>
                )}
                {intervenant.role && (
                  <div className="flex items-center mt-1">
                    <Shield className="text-blue-500 dark:text-blue-400 mr-1" size={14} />
                    <span className="text-blue-600 dark:text-blue-400 text-sm">
                      {getRoleName(intervenant.role)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Date d'effet de l'archivage */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <Calendar className="inline mr-2" size={16} />
              Date d'effet de l'archivage
            </label>
            <input
              type="date"
              value={archiveDate}
              onChange={(e) => setArchiveDate(e.target.value)}
              className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Les rendez-vous à partir de cette date seront libérés si l'option est cochée
            </p>
          </div>

          {/* Raison de l'archivage */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Raison de l'archivage *
            </label>
            <textarea
              value={archiveReason}
              onChange={(e) => setArchiveReason(e.target.value)}
              placeholder="Ex: Fin de contrat, Départ, Congé longue durée, Autre raison..."
              rows={3}
              className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          {/* Option de libération des rendez-vous */}
          <div className="flex items-start">
            <input
              type="checkbox"
              id="freeAppointments"
              checked={freeAppointments}
              onChange={(e) => setFreeAppointments(e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded mt-1"
            />
            <label htmlFor="freeAppointments" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
              Libérer tous les rendez-vous assignés à partir de la date choisie
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Les rendez-vous seront marqués comme "libres" et pourront être réassignés à d'autres intervenants
              </p>
            </label>
          </div>

          {/* Avertissement */}
          {freeAppointments && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-3">
              <div className="flex items-start">
                <AlertTriangle className="text-yellow-600 dark:text-yellow-400 mr-2 mt-0.5" size={16} />
                <div>
                  <p className="text-yellow-800 dark:text-yellow-200 text-sm font-medium">
                    Attention - Libération des rendez-vous
                  </p>
                  <p className="text-yellow-700 dark:text-yellow-300 text-xs mt-1">
                    Tous les rendez-vous de cet intervenant à partir du {archiveDate ? new Date(archiveDate).toLocaleDateString('fr-FR') : '...'} seront libérés et apparaîtront dans "Plages à combler".
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Message d'erreur */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-3">
              <p className="text-red-800 dark:text-red-200 text-sm">{error}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
              disabled={isLoading}
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isLoading || !archiveDate || !archiveReason.trim()}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:bg-orange-300 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? (
                <span className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Archivage...
                </span>
              ) : (
                'Archiver l\'intervenant'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ArchiveIntervenantModal;
